import { useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import { AutoComplete } from "@/core/components/ui/autocomplete/autocomplete";
import { Button } from "@/core/components/ui/button";
import { Label } from "@/core/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/core/components/ui/radio-group";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  useImpersonationBusinessOptions,
  useImpersonationTenantOptions,
  useImpersonationUserOptions,
} from "@/core/impersonation/impersonation-options";
import {
  getImpersonationUserOptions,
  type ImpersonationParentType,
} from "@/nav/impersonation-modal-utils";
import type { ImpersonationFormValues } from "./types";

export const ImpersonationFields = ({ open }: { open: boolean }) => {
  const { values, setValues, setFieldValue, isSubmitting } =
    useFormikContext<ImpersonationFormValues>();
  const { parentType, selectedParentId } = values;
  const parentInputRef = useRef<HTMLInputElement>(null);
  const initialFocusHandled = useRef(false);

  const tenantsQuery = useImpersonationTenantOptions(
    open && parentType === "tenant",
  );
  const businessesQuery = useImpersonationBusinessOptions(
    open && parentType === "business",
  );
  const userQuery = useImpersonationUserOptions(
    parentType,
    selectedParentId,
    open,
  );

  useEffect(() => {
    if (!open) {
      initialFocusHandled.current = false;
      return;
    }

    if (parentType !== "business") {
      // Switching to Tenant cancels the pending business focus for this opening.
      initialFocusHandled.current = true;
      return;
    }

    if (
      initialFocusHandled.current ||
      !businessesQuery.isSuccess ||
      isSubmitting
    ) {
      return;
    }

    const input = parentInputRef.current;
    if (input && !input.disabled) {
      input.focus();
      initialFocusHandled.current = true;
    }
  }, [open, parentType, businessesQuery.isSuccess, isSubmitting]);

  const handleParentTypeChange = (value: ImpersonationParentType) => {
    setValues(
      {
        ...values,
        parentType: value,
        selectedParentId: "",
        selectedUserId: "",
        parentSearchValue: "",
        userSearchValue: "",
      },
      true,
    );
  };

  const handleParentChange = (value: string) => {
    setValues(
      {
        ...values,
        selectedParentId: value,
        selectedUserId: "",
        userSearchValue: "",
      },
      true,
    );
  };

  const handleUserChange = (value: string) => {
    setFieldValue("selectedUserId", value, true);
  };

  const parentQuery = parentType === "tenant" ? tenantsQuery : businessesQuery;
  const userOptions = getImpersonationUserOptions(userQuery.data, parentType);

  return (
    <Stack className="p-6 pt-0" gap="lg" fullWidth>
      {/* Parent Type Selection */}
      <Stack gap="md" fullWidth>
        <Label>Select Type</Label>
        <RadioGroup
          value={values.parentType}
          onValueChange={handleParentTypeChange}
          className="flex gap-4"
          disabled={isSubmitting}
        >
          <Stack direction="row" align="center" gap="sm">
            <RadioGroupItem value="business" id="business" />
            <Label htmlFor="business" className="cursor-pointer">
              Business
            </Label>
          </Stack>
          <Stack direction="row" align="center" gap="sm">
            <RadioGroupItem value="tenant" id="tenant" />
            <Label htmlFor="tenant" className="cursor-pointer">
              Tenant
            </Label>
          </Stack>
        </RadioGroup>
      </Stack>

      {/* Parent Selection */}
      <AutoComplete
        ref={parentInputRef}
        ariaLabel={parentType === "tenant" ? "Tenant" : "Business"}
        selectedValue={values.selectedParentId}
        onSelectedValueChange={handleParentChange}
        searchValue={values.parentSearchValue}
        onSearchValueChange={(value) =>
          setFieldValue("parentSearchValue", value, false)
        }
        items={
          parentQuery.data?.map((p) => ({ value: p.id, label: p.name })) || []
        }
        placeholder={`Search ${values.parentType === "tenant" ? "tenants" : "businesses"}...`}
        label={values.parentType === "tenant" ? "Tenant" : "Business"}
        fullWidth
        isLoading={parentQuery.isLoading}
        disabled={isSubmitting || parentQuery.isLoading || parentQuery.isError}
      />

      {parentQuery.isLoading && (
        <Typography variant="bodyW3" textColor="secondary" role="status">
          Loading {parentType === "tenant" ? "tenants" : "businesses"}...
        </Typography>
      )}

      {parentQuery.isError && (
        <Stack fullWidth gap="xs" role="alert">
          <Typography variant="bodyW3" textColor="secondary">
            Could not load {parentType === "tenant" ? "tenants" : "businesses"}.
          </Typography>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={parentQuery.isFetching}
            onClick={() => void parentQuery.refetch()}
          >
            Try again
          </Button>
        </Stack>
      )}

      {/* User Selection */}
      <AutoComplete
        ariaLabel="User"
        emptyMessage="No eligible users."
        selectedValue={values.selectedUserId}
        onSelectedValueChange={handleUserChange}
        searchValue={values.userSearchValue}
        onSearchValueChange={(value) =>
          setFieldValue("userSearchValue", value, false)
        }
        items={userOptions}
        placeholder={
          !values.selectedParentId ? "Select a parent first" : "Search users..."
        }
        label="User"
        fullWidth
        isLoading={userQuery.isLoading}
        disabled={
          isSubmitting ||
          !selectedParentId ||
          userQuery.isLoading ||
          userQuery.isError
        }
      />
      {selectedParentId && userQuery.isLoading && (
        <Typography variant="bodyW3" textColor="secondary" role="status">
          Loading users...
        </Typography>
      )}
      {selectedParentId && userQuery.isError && (
        <Stack fullWidth gap="xs" role="alert">
          <Typography variant="bodyW3" textColor="secondary">
            Could not load users.
          </Typography>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={userQuery.isFetching}
            onClick={() => void userQuery.refetch()}
          >
            Try again
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
